import React from "react";
import {
    DataTable,
    Headline,
    IconButton,
    StyleFunction,
    Text,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import { AvailableLocale, useTranslation } from "locales";
import { View } from "react-native";
import { FiskalyEstablishmentInput } from "../fiskaly-form";
import { GQFiskalyEstablishment } from "graphql-sdk";
import { formatDate } from "lib";
import { useAdminSession } from "../../../../session";

export function FiskalyEstablishmentList({
    establishments: data,
    onEdit,
    onDelete,
}: {
    establishments: GQFiskalyEstablishment[];
    onEdit: (establishmentData: FiskalyEstablishmentInput) => void;
    onDelete: (establishmentId: string) => Promise<void>;
}) {
    const [t] = useTranslation();
    const theme = useTheme();
    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);

    return (
        <>
            <Headline size="h4">
                {t(
                    "backoffice.integration.fiskaly.establishment_table.header",
                    "Known establishments"
                )}
            </Headline>
            <DataTable>
                <DataTable.Header>
                    <DataTable.Title>
                        {t(
                            "backoffice.integration.fiskaly.establishment_table.name",
                            "Designation"
                        )}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t("backoffice.common.street", "Street")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t("backoffice.common.house_number", "House number")}
                    </DataTable.Title>
                    <DataTable.Title style={styles.zipColumn}>
                        {t("backoffice.common.zip", "Zip")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t("backoffice.common.town", "City")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t("backoffice.common.created_at", "Created at")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t("backoffice.common.updated_at", "Updated at")}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t(
                            "backoffice.integration.fiskaly.submit_before",
                            "Submit before"
                        )}
                    </DataTable.Title>
                    <DataTable.Title>
                        {t(
                            "backoffice.integration.fiskaly.submitted_at",
                            "Submitted at"
                        )}
                    </DataTable.Title>
                    <DataTable.Title numeric style={styles.iconColumn} />
                </DataTable.Header>
                {data.length === 0 ? (
                    <View>
                        <Text>
                            {t(
                                "backoffice.integration.fiskaly.establishment_table.no_rows",
                                "No establishments are created yet."
                            )}
                        </Text>
                    </View>
                ) : (
                    data.map((itm, idx) => (
                        <DataTable.Row
                            style={idx % 2 === 0 ? theme.styles.oddRow : null}
                            onPress={() =>
                                onEdit(itm as FiskalyEstablishmentInput)
                            }
                            key={`fiskaly_establishment_row_${idx}`}
                        >
                            <DataTable.Cell>{itm.designation}</DataTable.Cell>
                            <DataTable.Cell>{itm.street}</DataTable.Cell>
                            <DataTable.Cell>{itm.houseNumber}</DataTable.Cell>
                            <DataTable.Cell style={styles.zipColumn}>
                                {itm.zip}
                            </DataTable.Cell>
                            <DataTable.Cell>{itm.town}</DataTable.Cell>
                            <DataTable.Cell>
                                {itm.createdAt
                                    ? formatDate(
                                          itm.createdAt,
                                          locale as AvailableLocale
                                      )
                                    : null}
                            </DataTable.Cell>
                            <DataTable.Cell>
                                {itm.updatedAt
                                    ? formatDate(
                                          itm.updatedAt,
                                          locale as AvailableLocale
                                      )
                                    : null}
                            </DataTable.Cell>
                            <DataTable.Cell>
                                {itm.submitBefore
                                    ? formatDate(
                                          itm.submitBefore,
                                          locale as AvailableLocale
                                      )
                                    : null}
                            </DataTable.Cell>
                            <DataTable.Cell>
                                {itm.submittedAt
                                    ? formatDate(
                                          itm.submittedAt,
                                          locale as AvailableLocale
                                      )
                                    : null}
                            </DataTable.Cell>
                            <DataTable.Cell numeric style={styles.iconColumn}>
                                <IconButton
                                    color={theme.colors.secondary}
                                    name="edit"
                                    onPress={() =>
                                        onEdit(itm as FiskalyEstablishmentInput)
                                    }
                                />
                                <IconButton
                                    color={theme.colors.secondary}
                                    name="delete"
                                    onPress={async () => await onDelete(itm.id)}
                                />
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))
                )}
            </DataTable>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    zipColumn: {
        flexBasis: theme.spacingScale * 8,
        flexGrow: 0,
        flexShrink: 0,
    },
    iconColumn: {
        flexBasis: theme.spacingScale * 8,
        flexGrow: 0,
        flexShrink: 0,
    },
});
